import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, RefreshCw, Check, Save, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

const ALL_BRANDS = [
  "Nuki", "Tedee", "TTLock", "Igloohome", "Home Assistant", "KNX",
  "BACnet", "Philips Hue", "Somfy", "Yale", "Schlage",
  "Dormakaba", "Salto", "August", "Assa Abloy", "MQTT", "Zigbee2MQTT",
];

const BRAND_CATEGORIES = {
  "Smart Lock": ["Nuki", "Tedee", "TTLock", "Igloohome", "Yale", "Schlage", "August", "Assa Abloy", "Dormakaba", "Salto"],
  "Automazione": ["Home Assistant", "KNX", "BACnet", "Philips Hue", "Somfy"],
  "IoT / Protocolli": ["MQTT", "Zigbee2MQTT"],
};

export function CertifiedBrandsTab({ storedKey }) {
  const [certified, setCertified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  const headers = { 'X-Superadmin-Key': storedKey };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/certified-brands`, { headers });
      setCertified(data.certified_brands || []);
      setChanged(false);
    } catch {
      toast.error('Errore caricamento brand certificati.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (brand) => {
    setCertified(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
    setChanged(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/certified-brands`, { certified_brands: certified }, { headers });
      setCertified(data.certified_brands);
      setChanged(false);
      toast.success(`Badge aggiornati: ${data.certified_brands.length} brand certificati.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="certified-brands-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>
            Partner Certificati
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
            Attiva il badge dorato "Certified Partner" sulle guide del Blog per i brand verificati
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
            style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            <RefreshCw size={13} /> Ricarica
          </button>
          {changed && (
            <button
              data-testid="save-certified-brands-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium"
              style={{ background: 'var(--lux-gold)', color: '#000' }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salva modifiche
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 rounded-sm mb-6" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div className="flex items-start gap-3">
          <Info size={14} style={{ color: '#c9a84c', marginTop: 1, flexShrink: 0 }} />
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>
            I brand con badge "Certified Partner" mostrano un distintivo dorato sulle guide del Blog e nella pagina /developers.
            Certifica solo le integrazioni realmente testate e verificate dal team tecnico Shangrilux.
            Il badge aumenta la fiducia degli integratori tecnici e il tasso di conversione demo.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto"
            style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(BRAND_CATEGORIES).map(([cat, brands]) => (
            <div key={cat} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
              <p className="text-xs uppercase tracking-[3px] mb-4" style={{ color: 'var(--lux-gold)' }}>{cat}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {brands.map(brand => {
                  const isCertified = certified.includes(brand);
                  return (
                    <button
                      key={brand}
                      data-testid={`brand-toggle-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => toggle(brand)}
                      className="flex items-center gap-3 p-3 rounded-sm text-left transition-all"
                      style={{
                        background: isCertified ? 'rgba(201,168,76,0.1)' : 'var(--lux-surface-3)',
                        border: `1px solid ${isCertified ? 'rgba(201,168,76,0.35)' : 'var(--lux-border-2)'}`,
                        cursor: 'pointer',
                      }}>
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0"
                        style={{
                          background: isCertified ? 'var(--lux-gold)' : 'rgba(107,114,128,0.15)',
                          transition: 'background 0.2s',
                        }}>
                        {isCertified
                          ? <Award size={13} color="#000" />
                          : <span style={{ width: 13, height: 13 }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: isCertified ? 'var(--lux-gold)' : 'var(--lux-text-sec)' }}>
                          {brand}
                        </p>
                        {isCertified && (
                          <p className="text-[10px]" style={{ color: 'rgba(201,168,76,0.7)' }}>Certificato</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {!loading && (
        <div className="mt-6 flex items-center gap-3 p-4 rounded-sm"
          style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <Award size={16} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
            {certified.length === 0
              ? 'Nessun brand certificato. Seleziona i brand e salva.'
              : `${certified.length} brand certificato${certified.length > 1 ? 'i' : ''}: ${certified.join(', ')}`}
          </span>
          {changed && (
            <span className="text-xs ml-auto px-2 py-0.5 rounded"
              style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>
              Modifiche non salvate
            </span>
          )}
        </div>
      )}
    </div>
  );
}
